import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  Card, Typography, Stack, Box, FormControl, RadioGroup, Radio,
  FormControlLabel, TextField, Autocomplete,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LabelList, PieChart, Pie, Legend,
} from 'recharts';
import { cores } from '../styles/styleUtils';
import Iconify from '../components/iconify';

const CORES = [
  '#1565C0', '#2e7d32', '#6a1b9a', '#e65100', '#00838f',
  '#c62828', '#5d4037', '#455a64', '#7b1fa2', '#ef6c00',
];
const CORES_ETAPAS = ['#1565C0', '#1976D2', '#1E88E5', '#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB'];

const ETAPAS_SERVICO = [
  'Análise Contrato', 'Separação da Documentação', 'Execução do serviço',
  'Emissão ART', 'Protocolo', 'Emissão Documentação', 'Finalização',
];
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// Valor comercial de uma linha de serviço (recorrente multiplica pela duração)
const valorLinha = (s) =>
  (Number(s.valor) || 0) * (Number(s.quantidade) || 0) *
  (s.tipo === 'recorrente' ? (Number(s.duracao) || 1) : 1);

function KpiCard({ titulo, valor, icon, sub }) {
  return (
    <Card sx={{
      p: 2, minWidth: 150, flex: 1, textAlign: 'center',
      background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
      borderTop: `4px solid ${cores.laranja}`,
      borderRadius: 2, boxShadow: 3,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
    }}>
      {icon && <Iconify icon={icon} sx={{ color: cores.laranja, width: 22, height: 22 }} />}
      <Typography sx={{ color: '#bdbdbd', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {titulo}
      </Typography>
      <Typography sx={{ color: cores.textoClaro, fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
        {valor}
      </Typography>
      {sub && <Typography sx={{ color: '#9e9e9e', fontSize: 11 }}>{sub}</Typography>}
    </Card>
  );
}

export default function HomeEngenhariaServicos({ propostas, usuarios, isSupervisor }) {
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];

  const [filtro, setFiltro] = useState({ dataInicial: primeiroDia, dataFinal: ultimoDia, engenheiro: null });
  const [radioTop, setRadioTop] = useState('quantidade');

  // Apenas propostas de serviço (fluxo Engenharia de Serviços) + filtros locais.
  // O acesso por responsável/supervisor já vem resolvido do backend.
  const propostasServ = useMemo(() => (propostas || []).filter((p) => {
    if (p.fluxo?.id !== 100000000) return false;
    const d = new Date(p.data);
    const di = filtro.dataInicial ? new Date(filtro.dataInicial) : null;
    const df = filtro.dataFinal ? new Date(`${filtro.dataFinal}T23:59:59`) : null;
    if (di && d < di) return false;
    if (df && d > df) return false;
    if (filtro.engenheiro && p.responsavel?._id !== filtro.engenheiro._id) return false;
    return true;
  }), [propostas, filtro]);

  // KPIs
  const totalServicos = propostasServ.length;
  const totalReceita = propostasServ.reduce((s, p) => s + (Number(p.valorservico) || 0), 0);
  const ticketMedio = totalServicos ? totalReceita / totalServicos : 0;

  // Funil de serviços (7 etapas)
  const dadosFunil = useMemo(() => ETAPAS_SERVICO.map((nome, i) => ({
    name: nome,
    quantidade: propostasServ.filter((p) => p.etapa?.nome === nome).length,
    fill: CORES_ETAPAS[i % CORES_ETAPAS.length],
  })), [propostasServ]);

  // Agregação por tipo de serviço: quantidade (cada serviço da proposta conta 1)
  // e receita (valorservico rateado proporcionalmente ao valor de cada linha).
  const porTipo = useMemo(() => {
    const mapa = {};
    propostasServ.forEach((p) => {
      const linhas = p.servicos || [];
      const totalLinhas = linhas.reduce((s, x) => s + valorLinha(x), 0);
      const valorserv = Number(p.valorservico) || 0;
      linhas.forEach((s) => {
        const nome = s.nome || 'Outros';
        if (!mapa[nome]) mapa[nome] = { name: nome, quantidade: 0, receita: 0 };
        mapa[nome].quantidade += 1;
        const peso = totalLinhas > 0
          ? valorLinha(s) / totalLinhas
          : (linhas.length ? 1 / linhas.length : 0);
        mapa[nome].receita += valorserv * peso;
      });
    });
    return Object.values(mapa).map((v, i) => ({
      ...v,
      receita: parseFloat(v.receita.toFixed(2)),
      fill: CORES[i % CORES.length],
    }));
  }, [propostasServ]);

  const porTipoQtd = useMemo(
    () => [...porTipo].sort((a, b) => b.quantidade - a.quantidade),
    [porTipo],
  );
  const topServicos = useMemo(
    () => [...porTipo]
      .sort((a, b) => (radioTop === 'receita' ? b.receita - a.receita : b.quantidade - a.quantidade))
      .slice(0, 10),
    [porTipo, radioTop],
  );

  // Receita por semana do mês corrente
  const receitaSemanas = useMemo(() => {
    const semanas = { 'Semana 1': 0, 'Semana 2': 0, 'Semana 3': 0, 'Semana 4': 0, 'Semana 5': 0 };
    propostasServ.forEach((p) => {
      const d = new Date(p.data);
      if (d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()) {
        const semana = Math.min(5, Math.ceil(d.getDate() / 7));
        semanas[`Semana ${semana}`] += Number(p.valorservico) || 0;
      }
    });
    return Object.entries(semanas).map(([name, receita]) => ({ name, receita: parseFloat(receita.toFixed(2)) }));
  }, [propostasServ]);

  // Receita por mês
  const receitaMeses = useMemo(() => {
    const mapa = {};
    propostasServ.forEach((p) => {
      const d = new Date(p.data);
      const chave = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
      if (!mapa[chave]) {
        mapa[chave] = { chave, name: `${MESES[d.getMonth()]}/${String(d.getFullYear()).slice(2)}`, receita: 0 };
      }
      mapa[chave].receita += Number(p.valorservico) || 0;
    });
    return Object.values(mapa)
      .sort((a, b) => a.chave.localeCompare(b.chave))
      .map((v) => ({ ...v, receita: parseFloat(v.receita.toFixed(2)) }));
  }, [propostasServ]);

  return (
    <Box>
      {/* Filtro */}
      <Stack direction="row" gap={2} flexWrap="wrap" mb={3} alignItems="center">
        <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
          value={filtro.dataInicial} onChange={(e) => setFiltro((f) => ({ ...f, dataInicial: e.target.value }))} />
        <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
          value={filtro.dataFinal} onChange={(e) => setFiltro((f) => ({ ...f, dataFinal: e.target.value }))} />
        {isSupervisor && (
          <Autocomplete size="small" sx={{ minWidth: 220 }} options={usuarios || []}
            getOptionLabel={(o) => o.nome} value={filtro.engenheiro || null}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            onChange={(_, v) => setFiltro((f) => ({ ...f, engenheiro: v }))}
            renderInput={(p) => <TextField {...p} label="Engenheiro" />} />
        )}
      </Stack>

      {/* KPIs */}
      <Stack direction="row" gap={2} flexWrap="wrap" mb={4}>
        <KpiCard titulo="Serviços em Engenharia" valor={fmtNum(totalServicos)} icon="eva:briefcase-outline" />
        <KpiCard titulo="Receita (custo terceirização)" valor={fmtBRL(totalReceita)} icon="eva:trending-up-outline" />
        <KpiCard titulo="Ticket Médio" valor={fmtBRL(ticketMedio)} icon="eva:pie-chart-outline" />
      </Stack>

      {/* Funil de serviços */}
      <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
        <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>Funil de Serviços por Etapa</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dadosFunil} layout="vertical" margin={{ left: 60, right: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [`${v} serviço(s)`, 'Quantidade']} />
            <Bar dataKey="quantidade" radius={[0, 4, 4, 0]}>
              {dadosFunil.map((e, i) => <Cell key={i} fill={e.fill} />)}
              <LabelList dataKey="quantidade" position="right" style={{ fontSize: 12, fontWeight: 'bold' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Barras (tipos por quantidade) + Pizza */}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} mb={4}>
        <Card sx={{ p: 3, flex: 1, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>Tipos de Serviço por Quantidade</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={porTipoQtd} margin={{ left: 0, right: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={70} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(v) => [`${v} serviço(s)`, 'Quantidade']} />
              <Bar dataKey="quantidade" radius={[4, 4, 0, 0]}>
                {porTipoQtd.map((e, i) => <Cell key={i} fill={e.fill} />)}
                <LabelList dataKey="quantidade" position="top" style={{ fontSize: 11, fontWeight: 'bold' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card sx={{ p: 3, flex: 1, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>Distribuição de Tipos de Serviço</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={porTipoQtd} dataKey="quantidade" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => e.name}>
                {porTipoQtd.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} serviço(s)`, n]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </Stack>

      {/* Top serviços (quantidade × receita) */}
      <Card sx={{ p: 3, mb: 4, backgroundColor: '#F8F9FA' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0">Top Serviços Realizados</Typography>
          <FormControl>
            <RadioGroup row value={radioTop} onChange={(e) => setRadioTop(e.target.value)}>
              <FormControlLabel value="quantidade" control={<Radio size="small" />} label="Quantidade" />
              <FormControlLabel value="receita" control={<Radio size="small" />} label="Receita" />
            </RadioGroup>
          </FormControl>
        </Stack>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={topServicos} layout="vertical" margin={{ left: 60, right: 70 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(v) => (radioTop === 'receita' ? fmtBRL(v) : v)} />
            <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => (radioTop === 'receita' ? [fmtBRL(v), 'Receita'] : [`${v} serviço(s)`, 'Quantidade'])} />
            <Bar dataKey={radioTop} radius={[0, 4, 4, 0]}>
              {topServicos.map((e, i) => <Cell key={i} fill={e.fill} />)}
              <LabelList dataKey={radioTop} position="right" style={{ fontSize: 11, fontWeight: 'bold' }}
                formatter={(v) => (radioTop === 'receita' ? fmtBRL(v) : v)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Receita por semana + por mês */}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} mb={4}>
        <Card sx={{ p: 3, flex: 1, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>Receita por Semana (mês corrente)</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={receitaSemanas} margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => fmtBRL(v)} width={90} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [fmtBRL(v), 'Receita']} />
              <Bar dataKey="receita" fill="#2e7d32" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card sx={{ p: 3, flex: 1, backgroundColor: '#F8F9FA' }}>
          <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>Receita por Mês</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={receitaMeses} margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => fmtBRL(v)} width={90} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [fmtBRL(v), 'Receita']} />
              <Bar dataKey="receita" fill="#1565C0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Stack>
    </Box>
  );
}